import React, { useMemo } from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import { DashboardMetricTileGrid } from '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.component'
import type { UseProductionByDepartmentResult } from '@hooks/dashboard/useProductionByDepartment'
import {
  buildComparisonFooter,
  formatCurrency,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

type ProductionByDepartmentCardProps = {
  titleHref: string
  prodByDept: Pick<
    UseProductionByDepartmentResult,
    | 'loading'
    | 'hasPayload'
    | 'hasData'
    | 'dentistMetric'
    | 'hygienistMetric'
    | 'productionPerVisitMetric'
  >
}

function deptMetricTile(
  id: string,
  leftLabel: string,
  metric:
    | { current: number; previous: number; percentChange: number }
    | undefined,
  loading: boolean,
  formatValue: (n: number) => string = (n) =>
    formatCurrency(n, { compact: true }),
): DashboardMetricItemData {
  if (loading || !metric) {
    return { id, leftLabel, value: HYGIENE_PLACEHOLDER }
  }
  const footer = buildComparisonFooter(metric, { isCurrency: true })
  return {
    id,
    leftLabel,
    value: formatValue(metric.current),
    footer: footer.text,
    footerTrend: footer.up ? 'up' : 'down',
  }
}

export function ProductionByDepartmentCard({
  titleHref,
  prodByDept,
}: ProductionByDepartmentCardProps): React.ReactElement {
  const {
    loading,
    hasPayload,
    hasData,
    dentistMetric,
    hygienistMetric,
    productionPerVisitMetric,
  } = prodByDept
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  const metrics = useMemo(
    (): DashboardMetricItemData[] => [
      deptMetricTile('dentists', 'Dentists', dentistMetric, loading),
      deptMetricTile('hygienists', 'Hygienists', hygienistMetric, loading),
      deptMetricTile(
        'prodPerVisit',
        'Avg prod. / visit',
        productionPerVisitMetric,
        loading,
        (n) => formatCurrency(n),
      ),
    ],
    [loading, dentistMetric, hygienistMetric, productionPerVisitMetric],
  )

  return (
    <DashboardSectionCard
      title="Production by department"
      titleHref={titleHref}
      className="dashboard-performance-full-width dashboard-performance-production-dept-card"
      loading={pending}
      skeleton={{ metrics: 3, metricColumns: 3, block: 'none' }}
      isEmpty={isEmpty}
    >
      <DashboardMetricTileGrid metrics={metrics} columns={3} />
    </DashboardSectionCard>
  )
}
